import React, { forwardRef } from 'react';

import { Avatar, Box, Divider, Flex, Group, Menu, Space, Text, UnstyledButton } from '@mantine/core';
import { IconBook, IconChevronRight, IconLogout, IconRoute, IconSettings, IconTag } from '@tabler/icons-react';
import { LanguageChange } from 'components/LanguageChange';
import { UserButtonLoadingSkeleton } from 'components/LoadingSkeletons/UserButtonLoadingSkeleton';
import { useUserContext } from 'contexts/UserContext';
import { Key02Icon } from 'hugeicons-react';
import { GoogleDriveIcon } from 'icons/GoogleDriveIcon';
import { ZapierIcon } from 'icons/ZapierIcon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserService } from 'services/user-service';
import { DEFAULT_USER_AVATAR_KEY } from 'shared/constants/empty-user';
import { DOCS_URL, ROUTE_LINKS } from 'shared/constants/route-links';
import { IntegrationType } from 'shared/enums/integration';
import { UserAccess } from 'shared/interfaces/User';
import { formatInitials, formatName } from 'shared/utils/format';

interface MenuItemConfig {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  minAccessRole: UserAccess;
}

interface UserButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  image: string | null;
  firstName: string;
  lastName: string;
  email: string;
  icon?: React.ReactNode;
  isLoading?: boolean;
  collapsed?: boolean;
}

const UserButton = forwardRef<HTMLButtonElement, UserButtonProps>(
  ({ image, firstName, lastName, email, icon, isLoading = false, collapsed, ...others }: UserButtonProps, ref) => (
    <UnstyledButton
      ref={ref}
      py={10}
      sx={(theme) => ({
        display: 'block',
        width: '100%',
        color: theme.black,
        borderRadius: theme.radius.md,

        '&:hover': {
          backgroundColor: theme.colors.gray[0]
        }
      })}
      {...others}>
      {isLoading ? (
        <UserButtonLoadingSkeleton />
      ) : collapsed ? (
        <Avatar src={image} radius="xl" size="sm" ml="auto" mr="auto" my={1.4}>
          {formatInitials(firstName, lastName)}
        </Avatar>
      ) : (
        <Flex
          direction="row"
          align="center"
          justify="space-between"
          px={7}
          style={{ overflow: 'hidden' }}
          wrap={'nowrap'}>
          <Flex direction="row" align="center">
            <Avatar src={image} radius="xl" size="sm">
              {formatInitials(firstName, lastName)}
            </Avatar>

            <Space w={10} />

            <Flex direction="column">
              <Text size="xs" truncate maw={110}>
                {formatName(firstName, lastName)}
              </Text>

              <Text color="dimmed" size="xs" truncate maw={110}>
                {email}
              </Text>
            </Flex>
          </Flex>

          {icon || <IconChevronRight color="gray" style={{ width: 16, height: 16 }} />}
        </Flex>
      )}
    </UnstyledButton>
  )
);
UserButton.displayName = 'UserButton';

interface UserButtonWithMenuProps {
  collapsed?: boolean;
}

export function UserButtonWithMenu({ collapsed }: UserButtonWithMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [user] = useUserContext();
  const logout = () => {
    UserService.logout();
    navigate(ROUTE_LINKS.login);
  };

  const PRE_DIVIDER_MENU_ITEMS: MenuItemConfig[] = [
    {
      label: t('USER_BUTTON_MENU.SETTINGS'),
      icon: <IconSettings />,
      onClick: () => navigate(ROUTE_LINKS.generalSettings),
      minAccessRole: UserAccess.MEMBER
    },
    {
      label: t('USER_BUTTON_MENU.DOCUMENTATION'),
      icon: <IconBook />,
      onClick: () => window.open(DOCS_URL, '_blank'),
      minAccessRole: UserAccess.MEMBER
    },
    {
      label: t('USER_BUTTON_MENU.ZAPIER'),
      icon: (
        <Box mr={2}>
          <ZapierIcon w={20} h={17} />
        </Box>
      ),
      onClick: () => navigate(`${ROUTE_LINKS.integrations}?type=${IntegrationType.ZAPIER}`),
      minAccessRole: UserAccess.ADMIN
    },
    {
      label: t('USER_BUTTON_MENU.GOOGLE_DRIVE'),
      icon: (
        <Box mr={2}>
          <GoogleDriveIcon w={20} h={17} />
        </Box>
      ),
      onClick: () => navigate(`${ROUTE_LINKS.integrations}?type=${IntegrationType.GOOGLE_DRIVE}`),
      minAccessRole: UserAccess.ADMIN
    },
    {
      label: t('USER_BUTTON_MENU.API'),
      icon: (
        <Box mr={2}>
          <Key02Icon size={20} />
        </Box>
      ),
      onClick: () => navigate(ROUTE_LINKS.api),
      minAccessRole: UserAccess.ADMIN
    },
    {
      label: t('USER_BUTTON_MENU.WHITE_LABEL'),
      icon: <IconTag />,
      onClick: () => navigate(ROUTE_LINKS.whiteLabel),
      minAccessRole: UserAccess.SUPER_ADMIN
    },
    {
      label: t('USER_BUTTON_MENU.BYOK'),
      icon: <IconRoute />,
      onClick: () => navigate(ROUTE_LINKS.byok),
      minAccessRole: UserAccess.SUPER_ADMIN
    },
    {
      label: t('USER_BUTTON_MENU.LOG_OUT'),
      icon: <IconLogout />,
      onClick: logout,
      minAccessRole: UserAccess.MEMBER
    }
  ];

  const POST_DIVIDER_MENU_ITEMS: MenuItemConfig[] = [
    {
      label: t('USER_BUTTON_MENU.LOG_OUT'),
      icon: <IconLogout />,
      onClick: logout,
      minAccessRole: UserAccess.MEMBER
    }
  ];

  const hasMinAccess = (minAccessRole: UserAccess) => UserService.hasUserAccessWithMinRole(minAccessRole);
  const filteredPreDividerMenuItems = PRE_DIVIDER_MENU_ITEMS.filter((menuItem) => hasMinAccess(menuItem.minAccessRole));
  const filteredPostDividerMenuItems = POST_DIVIDER_MENU_ITEMS.filter((menuItem) =>
    hasMinAccess(menuItem.minAccessRole)
  );

  return (
    <Group position="center">
      <Menu width={250} position="right-end" offset={10} withinPortal={false} zIndex={2}>
        <Menu.Target>
          <UserButton
            image={user.avatarPath === DEFAULT_USER_AVATAR_KEY ? null : user.avatarPath}
            firstName={user.firstName}
            lastName={user.lastName}
            email={user.email}
            collapsed={collapsed}
          />
        </Menu.Target>
        <Menu.Dropdown style={{ zIndex: '1 !important' }}>
          {filteredPreDividerMenuItems.map((item) => (
            <Menu.Item key={item.label} my={5} onClick={item.onClick} icon={item.icon}>
              <Flex direction="row" align="center" justify="space-between">
                <Text>{item.label}</Text>
              </Flex>
            </Menu.Item>
          ))}

          <LanguageChange menuItem />
          <Divider />

          {filteredPostDividerMenuItems.map((item) => (
            <Menu.Item key={item.label} my={5} onClick={item.onClick} icon={item.icon}>
              <Flex direction="row" align="center" justify="space-between">
                <Text>{item.label}</Text>
              </Flex>
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
